import type {
  ClientGlobalRoleAssignmentRoleEnum,
  ClientOrganization
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useMemo } from 'react';

import { useOwnOrganizationRole } from '@/features/api/hooks/grapevine-organization/useOwnOrganizationRole';
import { useAppState } from '@/features/authorization/provider/useAppState';
import type { OrganizationAccessContextValue } from '@/shared/components/organization/organizationAccess';

/**
 * Fetches the raw data (global roles + own org role) and builds the
 * {@link OrganizationAccessContextValue} from it. The single place that does
 * the data fetching: `OrganizationAccessProvider` shares the value with a
 * subtree, `useOrganizationAccessFor` resolves it directly.
 */
export const useOrganizationAccessValue = (
  organization: ClientOrganization | undefined
): OrganizationAccessContextValue => {
  const { globalRoles } = useAppState();
  const { role: ownRole, isError } = useOwnOrganizationRole(
    organization?.id ?? ''
  );

  return useMemo(() => {
    const globalRoleValues: ClientGlobalRoleAssignmentRoleEnum[] = globalRoles
      .map((assignment) => assignment.role)
      .filter(
        (role): role is ClientGlobalRoleAssignmentRoleEnum => role !== undefined
      );

    return {
      isError,
      organization,
      globalRoles: globalRoleValues,
      ownRole
    };
  }, [globalRoles, isError, organization, ownRole]);
};
